"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, ShoppingBag, TrendingUp, CreditCard, Layers, ArrowRight, ShieldCheck, Search, CheckCircle2, SlidersHorizontal, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Measurement() {
  const shouldReduceMotion = useReducedMotion();
  const [activeLevelId, setActiveLevelId] = useState<string>("rankings");

  // Four distinct levels: Rankings, Traffic, Transactions, Revenue
  const performanceLevels = [{
    id: "rankings",
    label: "Rankings",
    kicker: "Level 1 • Search Visibility",
    icon: Search,
    summary: "Where your product and category pages position in search engines for specific commercial and informational search queries.",
    distinction: "Rankings are a leading indicator of visibility, not commercial return. Moving a collection page from page two to page one gives shoppers the opportunity to find your store, but a high position generates value only if shoppers actually click through and find what they want.",
    signals: ["Position shifts for priority commercial category terms (e.g. UK search queries)", "Keyword visibility across long-tail product attributes and SKU phrases", "Impressions and average position data tracked in Google Search Console", "Category-level rankings against direct search competitors"],
    attributionNote: "Rankings do not guarantee clicks or purchases. They measure search engine presence, not customer commitment."
  }, {
    id: "traffic",
    label: "Traffic",
    kicker: "Level 2 • Qualified Visits",
    icon: TrendingUp,
    summary: "The volume of organic sessions arriving on your product pages, category hubs, and supporting buying guides.",
    distinction: "Traffic proves that searchers saw your snippet and clicked through. However, high session volume from broad or mismatched queries can inflate visitor numbers without adding to your shopping basket. I focus on qualified traffic that aligns with purchase intent.",
    signals: ["Organic sessions landing directly on priority product and category pages", "Click-through rates (CTR) from Google Search Console queries", "First-time vs returning organic visitor proportions across collection hubs", "Engagement depth on commercial landing pages compared to informational content"],
    attributionNote: "Traffic is valuable only when the searcher's intent matches what your product or category page delivers."
  }, {
    id: "transactions",
    label: "Transactions",
    kicker: "Level 3 • Checkout Completions",
    icon: ShoppingBag,
    summary: "The number of completed orders and checkout actions initiated by shoppers who discovered your store through organic search.",
    distinction: "Transactions prove that visitors found the right product, accepted the pricing, and trusted the checkout process. SEO brings the right buyer to the right page, while on-page trust signals, shipping clarity, and mobile UX help complete the purchase.",
    signals: ["Direct transactions originating from organic product and category landing pages", "Assisted conversions where organic search introduced the customer earlier in the journey", "Organic conversion rate for key landing pages and product families", "Add-to-basket actions and checkout funnel progression from organic visits"],
    attributionNote: "Conversion rates depend on stock availability, pricing, shipping costs, and checkout usability alongside SEO."
  }, {
    id: "revenue",
    label: "Revenue",
    kicker: "Level 4 • Commercial Value",
    icon: CreditCard,
    summary: "The actual commercial income generated by organic transactions, tied to product margins and order values.",
    distinction: "Revenue is the ultimate commercial measure. Two stores can have similar transaction counts but very different revenue, which is why I take product margins into account when setting SEO priorities.",
    signals: ["Organic ecommerce revenue reported where analytics tracking is correctly configured", "Average order value (AOV) from organic search visitors across priority lines", "Revenue distribution across core categories compared to promotional or clearance lines", "Compounding return from evergreen collection and product page rankings"],
    attributionNote: "Attribution is never perfect. Multi-device shopping, cookie consent choices, and blended journeys mean directional trends matter more than exact last-click figures."
  }];
  const currentLevel = performanceLevels.find(level => level.id === activeLevelId) || performanceLevels[0];

  // Six conversion optimisation elements from source
  const conversionElements = [{
    number: "01",
    title: "Landing page intent match",
    description: "The specific query that brought a visitor must match the messaging, products, and filters they see on arrival, preventing immediate bounces."
  }, {
    number: "02",
    title: "Product information clarity",
    description: "Clear sizing charts, technical specifications, comparison tables, and authentic lifestyle imagery that answer pre-sale hesitations."
  }, {
    number: "03",
    title: "On-page trust signals",
    description: "Genuine customer reviews, security badges, visible business details, and real photography to give buyers confidence to add to basket."
  }, {
    number: "04",
    title: "Transparent shipping and returns",
    description: "Clear UK and international delivery options, dispatch timeframes, straightforward returns policies, and customs notes stated upfront."
  }, {
    number: "05",
    title: "Mobile shopping experience",
    description: "Clean navigation, sticky add-to-basket actions, thumb-friendly filters, and frictionless checkout steps designed for mobile screens."
  }, {
    number: "06",
    title: "Analytics and conversion tracking",
    description: "Configuring ecommerce tracking to evaluate which organic landing pages assist conversions and which generate direct checkout revenue."
  }];
  return <section id="measurement" data-nav="light" className="bg-muted/40 text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Commercial Measurement & CRO
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Measuring ecommerce SEO and conversion optimisation
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Search engine optimisation brings the right people to the right page; conversion
            optimisation helps them buy. I report on performance across four distinct commercial
            levels, separating search positions from actual commercial return.
          </p>
        </div>

        {/* Part 1: Four Levels of Performance */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
              Measuring ecommerce SEO performance across four levels
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Rankings, traffic, transactions, and revenue reflect different stages of search
              effectiveness. Select a level to see how I track and evaluate each one.
            </p>
          </div>

          {/* Interactive Level Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
            {performanceLevels.map((level, i) => {
            const Icon = level.icon;
            const isActive = level.id === activeLevelId;
            return <Button key={level.id} data-index={i} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveLevelId(level.id)} aria-pressed={isActive} className={`rounded-full px-4 sm:px-5 transition-all text-xs sm:text-sm font-medium flex items-center gap-2 ${isActive ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground bg-card"}`}>
                  <Icon className="size-3.5" />
                  <span>{level.label}</span>
                </Button>;
          })}
          </div>

          {/* Active Level Detail Panel */}
          <motion.div key={currentLevel.id} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="max-w-4xl mx-auto">
            <Card className="border border-border/80 bg-card shadow-xs">
              <CardHeader className="pb-4 border-b border-border/60">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <currentLevel.icon className="size-5" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                        {currentLevel.kicker}
                      </span>
                      <CardTitle className="text-xl font-semibold font-default text-foreground">
                        {currentLevel.label}
                      </CardTitle>
                    </div>
                  </div>
                  <Badge variant="secondary" className="self-start sm:self-center font-normal text-xs">
                    Plain-English reporting
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                    What this level represents
                  </h4>
                  <p className="text-sm sm:text-base text-foreground leading-relaxed">
                    {currentLevel.summary}
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-background/80 border border-border">
                  <h4 className="text-xs uppercase tracking-wider text-primary font-semibold mb-1.5 flex items-center gap-1.5">
                    <Info className="size-3.5" />
                    Why it differs from other levels
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {currentLevel.distinction}
                  </p>
                </div>

                <div className="pt-2">
                  <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                    Key performance indicators tracked
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {currentLevel.signals.map((signal, idx) => <div key={`signal-${idx}`} data-index={idx} className="p-3 rounded-md border border-border bg-background text-sm text-foreground flex items-start gap-2.5">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="leading-snug">{signal}</span>
                      </div>)}
                  </div>
                </div>

                <div className="pt-3 border-t border-border/60 flex items-start gap-2.5 text-xs text-muted-foreground">
                  <ShieldCheck className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-foreground font-medium">Reporting context:</strong>{" "}
                    {currentLevel.attributionNote}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Attribution Reality Note */}
          <div className="max-w-4xl mx-auto mt-6 p-4 rounded-lg border border-border bg-card/60 flex items-start gap-3">
            <Info className="size-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              <strong className="text-foreground font-medium">A note on attribution:</strong> Multi-channel customer
              journeys, cookie consent rates, and cross-device browsing affect analytics accuracy. I treat analytics
              directionally: long-term commercial trends matter far more than precise last-click figures. I do not promise
              specific sales figures, conversion rates, or rankings, but I ensure every recommendation is tied to genuine commercial opportunity.
            </p>
          </div>
        </div>

        {/* Part 2: Conversion Optimisation */}
        <div className="pt-12 border-t border-border/70">
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-2">
              <SlidersHorizontal className="size-3.5" />
              On-Page Alignment
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
              Ecommerce SEO and conversion optimisation
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Organic search brings qualified shoppers to your catalogue; on-page clarity turns them into customers.
              I review six critical touchpoints on your product and collection landing pages to remove friction before checkout:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {conversionElements.map((item, i) => <Card key={`cro-${i}`} data-index={i} className="border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-primary/10 text-primary">
                      {item.number}
                    </span>
                    <Layers className="size-4 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-base font-semibold text-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Link Row */}
        <div className="mt-14 pt-8 border-t border-border/60 text-center">
          <p className="text-sm text-muted-foreground">
            Looking for structured ongoing performance tracking and iterative growth? Learn more about my{" "}
            <Link to="/MonthlySeoServicesPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1">
              monthly SEO services
              <ArrowRight className="size-3.5" />
            </Link>
          </p>
        </div>
      </div>
    </section>;
}